import React, { lazy, Suspense, ComponentType } from 'react';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { LoadingSkeleton } from '@/components/ui/loading-skeleton';

interface LazyComponentOptions {
  fallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
  retryCount?: number;
  loadingTimeout?: number;
}

// Enhanced lazy loading with retry logic and better error handling
export function createLazyComponent<T extends Record<string, any> = {}>(
  importFn: () => Promise<{ default: ComponentType<T> }>,
  options: LazyComponentOptions = {}
): ComponentType<T> {
  // Add retry wrapper for failed imports
  const retryableImportFn = async (): Promise<{ default: ComponentType<T> }> => {
    const { retryCount = 3 } = options;
    let lastError: Error;
    
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        return await importFn();
      } catch (error) {
        lastError = error as Error;
        if (attempt < retryCount) {
          // Exponential backoff: 100ms, 400ms, 1600ms
          await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(4, attempt - 1)));
        }
      }
    }
    
    throw lastError!;
  };
  const {
    fallback,
    errorFallback,
    retryCount = 3,
    loadingTimeout = 2000, // Increased for better reliability
  } = options;

  const LazyComponent = lazy(retryableImportFn);

  return function EnhancedLazyComponent(props: T & any) {
    const defaultFallback = (
      <div className="min-h-[200px] flex items-center justify-center">
        <LoadingSkeleton variant="rectangular" className="w-full h-32" />
      </div>
    );

    const defaultErrorFallback = (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Component Loading Failed</div>
          <div className="text-muted-foreground">Please refresh the page to try again</div>
        </div>
      </div>
    );

    return (
      <ErrorBoundary fallback={errorFallback || defaultErrorFallback}>
        <Suspense fallback={fallback || defaultFallback}>
          <LazyComponent {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}

// Preload functions for critical routes
export const preloadComponent = (importFn: () => Promise<any>) => {
  // Preload on idle or with a small delay
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => importFn());
  } else {
    setTimeout(() => importFn(), 100);
  }
};

// Route-based preloading
export const preloadRouteComponents = () => {
  // Preload critical components on app initialization
  const criticalRoutes = [
    () => import('../pages/About'),
    () => import('../pages/Products'),
    () => import('../pages/Contact'),
    () => import('../pages/PricingPage'),
    // Added for sub-200ms hero paint
    () => import('../pages/PriceBeatGuarantee'),
    () => import('../pages/Profile'),
    () => import('../pages/Privacy'),
    () => import('../pages/Legal'),
    () => import('../pages/CookiePolicy'),
    () => import('../pages/GdprCompliance'),
    () => import('../pages/Analytics'),
    () => import('../pages/CheckMySite'),
  ];

  criticalRoutes.forEach(route => preloadComponent(route));
};

// Dynamic chunk names for better debugging
export const createNamedLazyComponent = <T extends Record<string, any> = {}>(
  importFn: () => Promise<{ default: ComponentType<T> }>,
  chunkName: string,
  options?: LazyComponentOptions
) => {
  // Add webpack magic comments for chunk naming
  const namedImportFn = () => importFn();
  return createLazyComponent(namedImportFn, options);
};